/**
 * @file Location Components
 * @description Location-related components and utilities for hierarchical location selection
 */

import React, { useState, useEffect, useRef } from 'react';

/**
 * Helper function to build hierarchical location tree structure
 *
 * @param {Array} locations - Flat array of location objects
 * @returns {Array} Hierarchical tree of locations
 *
 * @example
 * const tree = buildLocationTree([
 *   { id: '1', name: 'Project', parentId: null },
 *   { id: '2', name: 'Building A', parentId: '1' },
 *   { id: '3', name: 'Floor 1', parentId: '2' }
 * ]);
 */
export const buildLocationTree = (locations) => {
  // Create a map of locations by ID for quick lookup
  const locMap = {};
  locations.forEach(loc => {
    locMap[loc.id] = { ...loc, children: [] };
  });

  // Build the full hierarchy first
  const roots = [];
  locations.forEach(loc => {
    if (loc.parentId && locMap[loc.parentId]) {
      locMap[loc.parentId].children.push(locMap[loc.id]);
    } else {
      roots.push(locMap[loc.id]);
    }
  });

  // If there's a single root called "Project", skip it and return its children
  if (roots.length === 1 && roots[0].name && roots[0].name.toLowerCase() === 'project') {
    return roots[0].children || [];
  }

  return roots;
};

/**
 * LocationDropdown Component
 *
 * @component
 * @description A custom hierarchical location dropdown with expand/collapse functionality.
 * Features:
 * - Hierarchical tree structure with expandable nodes
 * - Position calculation to avoid overflow
 * - Click outside to close
 * - Clear selection option
 * - Selected item highlighting
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Currently selected location ID
 * @param {Function} props.onChange - Callback when location is selected
 * @param {Array} props.locations - Flat array of location objects
 * @param {Object} props.expandedLocations - Map of expanded location IDs
 * @param {Function} props.setExpandedLocations - Setter for expanded locations
 * @param {Function} props.onClose - Callback when dropdown should close
 * @param {React.RefObject} props.triggerRef - Reference to the trigger element
 *
 * @returns {JSX.Element} Location dropdown component
 *
 * @example
 * <LocationDropdown
 *   value={selectedLocationId}
 *   onChange={handleLocationChange}
 *   locations={locationsList}
 *   expandedLocations={expandedLocations}
 *   setExpandedLocations={setExpandedLocations}
 *   onClose={() => setDropdownOpen(false)}
 *   triggerRef={triggerButtonRef}
 * />
 */
export const LocationDropdown = ({
  value,
  onChange,
  locations,
  expandedLocations,
  setExpandedLocations,
  onClose,
  triggerRef
}) => {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });

  // Calculate position based on available space
  useEffect(() => {
    if (triggerRef?.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 400; // max height of dropdown
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      setPosition({
        top: openUpward ? triggerRect.top - Math.min(dropdownHeight, spaceAbove) : triggerRect.bottom + 4,
        left: triggerRect.left,
        openUpward
      });
    }
  }, [triggerRef]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const toggleExpand = (locId) => {
    setExpandedLocations(prev => ({
      ...prev,
      [locId]: !prev[locId]
    }));
  };

  const renderLocationItem = (loc, level = 0) => {
    const hasChildren = loc.children && loc.children.length > 0;
    const isExpanded = expandedLocations[loc.id];
    const isSelected = value === loc.id;

    return (
      <div key={loc.id}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            paddingLeft: `${12 + level * 20}px`,
            cursor: 'pointer',
            backgroundColor: isSelected ? '#dbeafe' : 'transparent',
            borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
            transition: 'all 0.15s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#dbeafe' : '#f1f5f9'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#dbeafe' : 'transparent'}
        >
          {hasChildren && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(loc.id);
              }}
              style={{
                marginRight: 8,
                fontSize: '0.75rem',
                color: '#64748b',
                cursor: 'pointer',
                userSelect: 'none',
                width: 16,
                display: 'inline-block'
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          {!hasChildren && <span style={{ width: 16, marginRight: 8, display: 'inline-block' }}></span>}
          <span
            onClick={() => onChange(loc.id)}
            style={{
              flex: 1,
              fontSize: '0.9rem',
              color: isSelected ? '#1e40af' : '#334155',
              fontWeight: isSelected ? 700 : 400
            }}
          >
            {loc.name}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {loc.children.map(child => renderLocationItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const tree = buildLocationTree(locations);

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        minWidth: '350px',
        backgroundColor: '#fff',
        border: '2px solid #3b82f6',
        borderRadius: 8,
        boxShadow: '0 10px 25px rgba(0,0,0,0.2), 0 6px 10px rgba(0,0,0,0.15)',
        maxHeight: 400,
        overflowY: 'auto',
        zIndex: 9999
      }}
    >
      <div
        onClick={() => onChange('')}
        style={{
          padding: '10px 12px',
          cursor: 'pointer',
          borderBottom: '2px solid #e2e8f0',
          fontSize: '0.9rem',
          color: '#64748b',
          fontStyle: 'italic',
          fontWeight: 600,
          position: 'sticky',
          top: 0,
          backgroundColor: '#f8fafc',
          zIndex: 1
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
      >
        ✕ Clear Selection
      </div>
      {tree.map(loc => renderLocationItem(loc, 0))}
    </div>
  );
};

export default LocationDropdown;
